import React, { useState } from "react";
import {
  LayoutDashboard,
  Users,
  HardHat,
  FileText,
  AlertTriangle,
  BarChart3,
  Settings,
  ChevronLeft,
  Search,
  Filter,
  Download,
  Upload,
  Plus,
  MoreVertical,
  Edit3,
  Trash2,
  Calendar,
  Clock,
  CheckSquare,
  Camera,
  X,
  Menu,
  Bell,
} from "lucide-react";

// --- Components ---

const SidebarItem = ({ icon: Icon, label, active, onClick, collapsed }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
      active
        ? "text-blue-600 bg-blue-50 border-r-2 border-blue-600"
        : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"
    }`}
  >
    <Icon size={20} strokeWidth={2} />
    {!collapsed && <span>{label}</span>}
  </button>
);

const StatCard = ({ title, count, icon: Icon, colorClass, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
  >
    <div className="flex justify-between items-start">
      <div>
        <p className="text-slate-500 text-sm font-medium mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-slate-900">{count}</h3>
      </div>
      <div
        className={`p-3 rounded-xl ${colorClass} bg-opacity-10 text-opacity-100 group-hover:scale-110 transition-transform`}
      >
        <Icon size={24} className={colorClass.replace("bg-", "text-")} />
      </div>
    </div>
    {/* Decorative background circle */}
    <div
      className={`absolute -bottom-4 -right-4 w-24 h-24 rounded-full ${colorClass} opacity-5`}
    ></div>
  </div>
);

const Badge = ({ count, color }) => {
  if (!count || count === 0) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {count}
    </span>
  );
};

// --- Views ---

const DashboardView = ({ onNavigate }) => {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-1">
            <span>Projects</span>
            <span>/</span>
            <span className="text-slate-900 font-medium">MML 7A CA 48</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            Project Overview
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
            Mumbai
          </span>
          <button className="text-slate-400 hover:text-slate-600">
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Learning & Development */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
          <h2 className="text-lg font-semibold text-slate-800">
            Learning & Development
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="TBT Meetings"
            count="290"
            icon={Users}
            colorClass="bg-blue-500"
            onClick={() => onNavigate("tbt-list")}
          />
          <StatCard
            title="Safety Induction"
            count="21"
            icon={HardHat}
            colorClass="bg-indigo-500"
          />
          <StatCard
            title="Specific Trainings"
            count="87"
            icon={FileText}
            colorClass="bg-violet-500"
          />
          <StatCard
            title="Safety Events"
            count="1"
            icon={Calendar}
            colorClass="bg-purple-500"
          />
        </div>
      </section>

      {/* Operations & Compliance */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 bg-orange-500 rounded-full"></div>
          <h2 className="text-lg font-semibold text-slate-800">
            Operations & Compliance
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Work Permits"
            count="1"
            icon={CheckSquare}
            colorClass="bg-orange-500"
          />
          <StatCard
            title="Safety Checklists"
            count="1"
            icon={FileText}
            colorClass="bg-amber-500"
          />
          <StatCard
            title="Observations"
            count="59"
            icon={AlertTriangle}
            colorClass="bg-red-500"
          />
          <StatCard
            title="Violation Notices"
            count="0"
            icon={AlertTriangle}
            colorClass="bg-rose-500"
          />
          <StatCard
            title="Incident Reports"
            count="0"
            icon={Users}
            colorClass="bg-pink-500"
          />
        </div>
      </section>

      {/* Analytics */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-6 bg-emerald-500 rounded-full"></div>
          <h2 className="text-lg font-semibold text-slate-800">
            Analytics & Insights
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Staff Rating"
            count="--"
            icon={BarChart3}
            colorClass="bg-emerald-500"
          />
          <StatCard
            title="HSE Budget"
            count="₹ 0"
            icon={FileText}
            colorClass="bg-teal-500"
          />
          <StatCard
            title="Safety Reportings"
            count="0"
            icon={BarChart3}
            colorClass="bg-cyan-500"
          />
        </div>
      </section>
    </div>
  );
};

const TBTListView = ({ onNavigate }) => {
  const data = [
    {
      id: 1,
      topic: "Housekeeping at site",
      date: "12 Feb 2026",
      time: "8:30 AM",
      count: 185,
      attendees: "Staff, Workers",
    },
    {
      id: 2,
      topic: "Hot work safety",
      date: "11 Feb 2026",
      time: "8:30 AM",
      count: 175,
      attendees: "Staff, Workers",
    },
    {
      id: 3,
      topic: "Working at heights",
      date: "11 Feb 2026",
      time: "8:52 AM",
      count: 126,
      attendees: "Staff, Workers, Clients",
    },
    {
      id: 4,
      topic: "Importance of PPEs",
      date: "10 Feb 2026",
      time: "8:30 AM",
      count: 192,
      attendees: "Staff, Workers",
    },
    {
      id: 5,
      topic: "Electrical safety",
      date: "09 Feb 2026",
      time: "9:02 AM",
      count: 113,
      attendees: "Staff, Workers, Clients",
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <button
            onClick={() => onNavigate("dashboard")}
            className="flex items-center text-slate-500 hover:text-slate-900 text-sm mb-2"
          >
            <ChevronLeft size={16} /> Back to Dashboard
          </button>
          <h1 className="text-2xl font-bold text-slate-900">TBT Meetings</h1>
        </div>
        <button
          onClick={() => onNavigate("edit")}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 shadow-lg shadow-blue-200 transition-all"
        >
          <Plus size={18} /> Add New Entry
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-2">
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Download size={16} /> Export
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
              <Filter size={16} /> Filter
            </button>
          </div>
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search topics..."
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider font-semibold">
                <th className="p-4 w-10">
                  <input type="checkbox" className="rounded border-slate-300" />
                </th>
                <th className="p-4">Topic Types</th>
                <th className="p-4">Date & Time</th>
                <th className="p-4 text-center">Count</th>
                <th className="p-4">Attendees Group</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-slate-50/50 transition-colors group"
                >
                  <td className="p-4">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                    />
                  </td>
                  <td className="p-4">
                    <span className="font-medium text-slate-900">
                      {item.topic}
                    </span>
                    <div className="text-xs text-slate-500 mt-1">
                      MML 7A CA 48
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                      <Calendar size={14} className="text-slate-400" />{" "}
                      {item.date}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                      <Clock size={14} className="text-slate-400" /> {item.time}
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {item.count}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex -space-x-2">
                      {item.attendees.split(",").map((a, i) => (
                        <div
                          key={i}
                          className="w-6 h-6 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-[10px] font-bold text-slate-600"
                          title={a.trim()}
                        >
                          {a.trim().charAt(0)}
                        </div>
                      ))}
                    </div>
                    <span className="text-xs text-slate-500 mt-1 block">
                      {item.attendees}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => onNavigate("edit")}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const EditView = ({ onNavigate }) => {
  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <button
        onClick={() => onNavigate("tbt-list")}
        className="flex items-center text-slate-500 hover:text-slate-900 text-sm"
      >
        <ChevronLeft size={16} /> Back to List
      </button>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              Edit TBT Meeting
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Update meeting details and evidence
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => onNavigate("tbt-list")}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md shadow-blue-200 transition-all">
              Save Changes
            </button>
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Project
              </label>
              <select className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none bg-white">
                <option>MML 7A CA 48</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Topic Types
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                  Housekeeping at site{" "}
                  <button className="ml-2 hover:text-blue-900">
                    <X size={12} />
                  </button>
                </span>
              </div>
              <button className="text-sm text-blue-600 font-medium hover:underline">
                + Select Topic Types
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    defaultValue="2026-02-12"
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-slate-600"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Time
                </label>
                <div className="flex gap-2">
                  <select className="w-full px-2 py-2.5 rounded-lg border border-slate-200 text-sm">
                    <option>08</option>
                  </select>
                  <select className="w-full px-2 py-2.5 rounded-lg border border-slate-200 text-sm">
                    <option>30</option>
                  </select>
                  <select className="w-full px-2 py-2.5 rounded-lg border border-slate-200 text-sm">
                    <option>AM</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Conducted By
              </label>
              <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-xs">
                  CK
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    Chandan Kumar Prasad
                  </p>
                  <p className="text-xs text-slate-500">Safety Officer</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Attendance
              </label>
              <div className="flex gap-4 mb-4">
                <div className="flex-1">
                  <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">
                    Total Count
                  </span>
                  <input
                    type="number"
                    defaultValue="185"
                    className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 font-semibold text-slate-900"
                  />
                </div>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <label className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />{" "}
                  Select All
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {["Staff", "Workers", "Suppliers", "Clients", "Visitors"].map(
                    (type) => (
                      <label
                        key={type}
                        className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          defaultChecked={["Staff", "Workers"].includes(type)}
                          className="rounded text-blue-600 focus:ring-blue-500"
                        />
                        {type}
                      </label>
                    ),
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Evidence Photo
              </label>
              <div className="relative group rounded-xl overflow-hidden border-2 border-dashed border-slate-200 hover:border-blue-400 transition-colors bg-slate-50 min-h-[160px] flex items-center justify-center">
                {/* Mock Image Placeholder */}
                <img
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=600"
                  alt="Site"
                  className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center flex-col text-white">
                  <Camera size={24} className="mb-2" />
                  <span className="text-xs font-medium">Change Photo</span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">
                Supported: JPG, PNG (Max 5MB)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Comments
              </label>
              <textarea
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-sm min-h-[80px]"
                defaultValue="UG station site work proceeding smoothly."
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Layout ---

export default function Testing() {
  const [currentView, setCurrentView] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900">
      {/* Sidebar */}
      <aside
        className={`bg-white border-r border-slate-200 flex flex-col transition-all duration-300 ${sidebarCollapsed ? "w-20" : "w-64"}`}
      >
        <div className="p-4 flex items-center justify-between">
          <div
            className={`flex items-center gap-3 ${sidebarCollapsed ? "justify-center w-full" : ""}`}
          >
            <div className="w-10 h-10 bg-yellow-400 rounded-lg flex items-center justify-center shadow-sm">
              <span className="font-bold text-red-700 text-xl">J</span>
            </div>
            {!sidebarCollapsed && (
              <span className="font-bold text-lg tracking-tight">J-Safety</span>
            )}
          </div>
        </div>

        <nav className="flex-1 py-6 space-y-1 overflow-y-auto">
          <SidebarItem
            icon={LayoutDashboard}
            label="Dashboard"
            active={currentView === "dashboard"}
            onClick={() => setCurrentView("dashboard")}
            collapsed={sidebarCollapsed}
          />
          <div className="px-4 py-2 mt-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden md:block">
            {!sidebarCollapsed && "Management"}
          </div>
          <SidebarItem
            icon={Users}
            label="TBT Meetings"
            active={currentView.includes("tbt")}
            onClick={() => setCurrentView("tbt-list")}
            collapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={HardHat}
            label="Inductions"
            collapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={FileText}
            label="Trainings"
            collapsed={sidebarCollapsed}
          />
          <div className="px-4 py-2 mt-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hidden md:block">
            {!sidebarCollapsed && "Compliance"}
          </div>
          <SidebarItem
            icon={CheckSquare}
            label="Permits"
            collapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={AlertTriangle}
            label="Incidents"
            collapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={BarChart3}
            label="Analytics"
            collapsed={sidebarCollapsed}
          />
        </nav>

        <div className="p-4 border-t border-slate-100">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 text-slate-400 hover:bg-slate-50 rounded-lg"
          >
            <Menu size={20} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 sticky top-0 z-10">
          <div className="w-1/3">
            {/* Breadcrumbs or Title could go here */}
          </div>
          <div className="flex items-center gap-4">
            <button className="relative p-2 text-slate-400 hover:text-slate-600 transition-colors">
              <Bell size={20} />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
            </button>
            <div className="flex items-center gap-3 pl-4 border-l border-slate-100">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-slate-900">
                  Chandan Kumar
                </p>
                <p className="text-xs text-slate-500">Admin</p>
              </div>
              <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm">
                CK
              </div>
            </div>
          </div>
        </header>

        <div className="p-8 max-w-7xl mx-auto">
          {currentView === "dashboard" && (
            <DashboardView onNavigate={setCurrentView} />
          )}
          {currentView === "tbt-list" && (
            <TBTListView onNavigate={setCurrentView} />
          )}
          {currentView === "edit" && <EditView onNavigate={setCurrentView} />}
        </div>
      </main>
    </div>
  );
}
